import { Building2, MapPin, Monitor, Plus, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { AddCustomerDialog, useOwnsEveryCustomer } from '../customers/AddCustomer';
import { ArchivedCustomers } from '../customers/ArchivedCustomers';
import { recordPath } from '../records/paths';
import { useScope } from '../scope/scope';
import { fleetQuery, needsLookQuery, offlineQuery, useCatalog, useFacets, useSearch } from '../search/hooks';
import { Button } from '../ui/Button';
import { cn } from '../ui/cn';
import { ErrorPanel } from '../ui/errors';
import { Card, EmptyState, Skeleton } from '../ui/misc';
import { type Status, StatusBar, toStatus } from '../ui/status';
import { PageHeader } from './PageHeader';

export function CustomersPage() {
  const { mspId } = useScope();
  const catalog = useCatalog();
  const fleet = useFacets(fleetQuery);
  const needs = useSearch(needsLookQuery());
  // Every offline device, servers included, per customer: shown as a plain status in each bar (WRMM-255).
  const offline = useFacets(offlineQuery);
  const [text, setText] = useState('');
  const owner = useOwnsEveryCustomer();
  const [adding, setAdding] = useState(false);
  const rows = useMemo(() => {
    const devices = new Map(fleet.data?.organizations.map(o => [o.id, o.count]));
    const sites = new Map<string, number>();
    for (const s of catalog.catalog.sites) sites.set(s.organizationId, (sites.get(s.organizationId) ?? 0) + 1);
    const offlineBy = new Map(offline.data?.organizations.map(o => [o.id, o.count]));
    const flagged = new Map<string, Record<Status, number>>();
    for (const h of needs.data?.hits ?? []) {
      const counts = flagged.get(h.organizationId) ?? { healthy: 0, attention: 0, offline: 0, unknown: 0 };
      // Stale "healthy" devices are shown as unknown: their status can't be vouched for.
      counts[h.status === 'healthy' ? 'unknown' : toStatus(h.status)] += 1;
      flagged.set(h.organizationId, counts);
    }
    return catalog.catalog.organizations.map(o => {
      const total = devices.get(o.id) ?? 0;
      const f = flagged.get(o.id) ?? { healthy: 0, attention: 0, offline: 0, unknown: 0 };
      // f.offline is offline servers (the only offline devices that need a look); the rest offline are just a status.
      const needing = f.attention + f.offline + f.unknown;
      const allOffline = Math.max(f.offline, offlineBy.get(o.id) ?? 0);
      return { ...o, total, sites: sites.get(o.id) ?? 0,
        counts: { ...f, offline: allOffline, healthy: Math.max(0, total - needing - (allOffline - f.offline)) }, needing };
    }).sort((a, b) => b.needing - a.needing || a.title.localeCompare(b.title));
  }, [catalog.catalog, fleet.data, needs.data, offline.data]);
  const shown = rows.filter(r => r.title.toLowerCase().includes(text.trim().toLowerCase()));
  const loading = catalog.isPending || fleet.isPending || needs.isPending || offline.isPending;
  const error = catalog.error ?? fleet.error ?? needs.error ?? offline.error;

  return (
    <div className="space-y-5">
      <PageHeader title="Customers" description={loading ? 'Loading…' : `${rows.length} customers · sorted by what needs a look`}
        actions={owner && <Button variant="primary" onClick={() => setAdding(true)}><Plus className="size-4" />Add customer</Button>} />
      {owner && <AddCustomerDialog open={adding} onOpenChange={setAdding} />}
      <label className="relative flex max-w-md items-center">
        <Search className="pointer-events-none absolute left-3 size-4 text-ink-3" />
        <input data-page-search value={text} onChange={e => setText(e.target.value)} placeholder="Find a customer" aria-label="Find a customer"
          className="h-10 w-full rounded-xl border border-line bg-panel pl-9 pr-3 shadow-card outline-none focus:border-brand/50 focus:ring-4 focus:ring-brand/10" />
      </label>
      {error ? <Card><ErrorPanel error={error} onRetry={() => { void catalog.refetch(); void fleet.refetch(); void needs.refetch(); void offline.refetch(); }} /></Card>
        : loading ? <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">{[0, 1, 2, 3].map(i => <Card key={i} className="space-y-3 p-5"><Skeleton className="h-5 w-1/2" /><Skeleton className="h-2 w-full" /><Skeleton className="w-1/3" /></Card>)}</div>
        : !shown.length ? <Card><EmptyState icon={<Building2 className="size-5" />} title={rows.length ? 'No customers match' : 'No customers yet'}>{rows.length ? 'Try a different name.' : owner ? 'Add a customer and its first site, then download an installer for it in Settings → Add devices.' : 'Customers you can access will appear here.'}</EmptyState></Card>
        : (
          <ul className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
            {shown.map(r => (
              <li key={r.id}>
                <Link to={recordPath(mspId, 'organization', r.id)}
                  className="group block h-full rounded-xl border border-line bg-panel p-5 shadow-card transition-[border-color,transform] hover:-translate-y-px hover:border-line-strong">
                  <div className="flex items-start gap-3">
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-sunken text-[13px] font-semibold text-ink-2">{initials(r.title)}</span>
                    <div className="min-w-0 flex-1">
                      <h2 className="truncate text-[15px] font-semibold group-hover:underline">{r.title}</h2>
                      <p className="mt-0.5 flex gap-3 text-[12.5px] text-ink-3">
                        <span className="inline-flex items-center gap-1"><MapPin className="size-3.5" />{r.sites} site{r.sites === 1 ? '' : 's'}</span>
                        <span className="inline-flex items-center gap-1"><Monitor className="size-3.5" />{r.total} device{r.total === 1 ? '' : 's'}</span>
                      </p>
                    </div>
                  </div>
                  <StatusBar className="mt-4" counts={r.counts} quietOffline />
                  <p className={cn('mt-2.5 text-[13px] font-medium', r.needing ? 'text-attention' : r.total ? 'text-healthy' : 'text-ink-3')}>
                    {r.needing ? `${r.needing} need${r.needing === 1 ? 's' : ''} a look` : !r.total ? 'No devices yet'
                      : r.counts.offline ? `Nothing needs a look · ${r.counts.offline} offline` : 'All devices healthy and checking in'}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      {owner && <ArchivedCustomers />}
    </div>
  );
}

const initials = (name: string) => name.split(/\s+/).filter(w => /^[A-Za-z0-9]/.test(w)).slice(0, 2).map(w => w[0]!.toUpperCase()).join('');
